import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { AiFillCloseCircle, AiOutlineUpload } from "react-icons/ai";
import { MdMailOutline } from "react-icons/md";
import {
  apiPostPut,
  apiGet,
  apiPostBlob,
  apiGetBlob,
} from "../../api/api_methods";
import { rolesWithId } from "../../constants/constants";
import constants from "../../api/api_urls";
import { uploadAttachmentsToAzure } from "../../utils/azureAttachments";
import { useStores } from "../../stores";

const MAX_ATTACHMENTS = 3;

const EMPTY_SUMMARY_COMMENTS = {
  awareness: "",
  wings: "",
  alertness: "",
  knowledge: "",
  execution: "",
  investNow: "",
};

const SUMMARY_COMMENT_FIELDS = [
  { key: "awareness", label: "Section 1 — Awareness" },
  { key: "wings", label: "Section 2 — Wings (goals)" },
  { key: "alertness", label: "Section 3 — Alertness (cash flow)" },
  { key: "knowledge", label: "Section 4 — Knowledge / risk" },
  { key: "execution", label: "Section 5 — Execution" },
  { key: "investNow", label: "Section 6 — Invest in the NOW" },
];

const TEMPLATES = [
  {
    name: "Finance Report Summary",
    subject: "Your Monthly Finance Report is Ready - [CLIENT_NAME]",
    html: `<p>Dear [CLIENT_NAME],</p>
<p>We are pleased to inform you that your monthly finance report is now available. This report provides a comprehensive overview of your savings, investments, and recent transactions for the month.</p>
<p>Total Savings: $12,500</p>
<p>New Deposits: $1,200</p>
<p>Withdrawals: $300</p>
<p>Interest Earned: $45</p>
<p>For a detailed breakdown and personalized insights, please <a href='[FORM_LINK]'>click here</a> to access your full report.</p>
<p>If you have any questions or need further assistance, feel free to reply to this email or contact your financial advisor.</p>
<p>Thank you for choosing Awaken Financial.<br/>Best regards,<br/>Awaken Financial Team</p>`,
  },
  {
    name: "Yearly Savings Overview",
    subject: "Your Yearly Savings Overview - [CLIENT_NAME]",
    html: `<p>Hello [CLIENT_NAME],</p>
<p>Congratulations on another successful year of savings! Your annual savings overview is now ready and includes a summary of your financial progress, key highlights, and recommendations for the upcoming year.</p>
<p>Total Savings This Year: $15,800</p>
<p>Average Monthly Deposit: $1,316</p>
<p>Top Performing Account: High-Yield Savings</p>
<p>To view your complete annual report, including charts and personalized advice, please <a href='[FORM_LINK]'>click here</a>.</p>
<p>We appreciate your trust in Awaken Financial. Should you have any questions or wish to schedule a review meeting, please let us know.</p>
<p>Warm regards,<br/>Awaken Financial Team</p>`,
  },
  {
    name: "Investment Portfolio Review",
    subject: "Your Investment Portfolio Review is Ready - [CLIENT_NAME]",
    html: `<p>Dear [CLIENT_NAME],</p>
<p>Your comprehensive investment portfolio review has been completed and is now available for your review. This detailed analysis covers your current investments, performance metrics, and strategic recommendations for the upcoming quarter.</p>
<p>Portfolio Performance: +8.5% YTD</p>
<p>Risk Assessment: Moderate</p>
<p>Recommended Actions: Rebalance portfolio, consider new opportunities</p>
<p>To access your complete investment portfolio review and personalized recommendations, please <a href='[FORM_LINK]'>click here</a>.</p>
<p>Our team is available to discuss any questions you may have about your investment strategy.</p>
<p>Best regards,<br/>Awaken Financial Investment Team</p>`,
  },
  {
    name: "Financial Planning Assessment",
    subject: "Your Financial Planning Assessment Report - [CLIENT_NAME]",
    html: `<p>Hello [CLIENT_NAME],</p>
<p>Your personalized financial planning assessment has been completed! This comprehensive report analyzes your current financial situation, identifies areas for improvement, and provides actionable steps to achieve your financial goals.</p>
<p>Current Financial Health Score: 7.8/10</p>
<p>Key Areas of Focus: Emergency fund, retirement planning, debt management</p>
<p>Next Steps: Schedule consultation, implement recommendations</p>
<p>To review your complete financial planning assessment and personalized action plan, please <a href='[FORM_LINK]'>click here</a>.</p>
<p>We're excited to help you build a stronger financial future. Please don't hesitate to reach out with any questions.</p>
<p>Warm regards,<br/>Awaken Financial Planning Team</p>`,
  },
];



export default function MailPreview(props) {
  const { clientsStore } = useStores();
  console.log("MailPreview props received:", props);

  const [emailList, setEmailList] = useState(props.emails || []);
  const [subject, setSubject] = useState(props.sub || "Sample Subject");
  const [message, setMessage] = useState(
    props.mess || "Sample message content..."
  );
  const [file, setFile] = useState([]);
  const [loader, setLoader] = useState(false);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [fontSize, setFontSize] = useState("16px");
  const [fontFamily, setFontFamily] = useState("Arial");
  const [newEmail, setNewEmail] = useState("");
  const editorRef = useRef(null);
  const [showTemplateModal, setShowTemplateModal] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null); // index for local TEMPLATES array
  const [previewIdx, setPreviewIdx] = useState(null);
  const [clientDetails, setClientDetails] = useState({
    name: props.clientName || "Client",
    planType: props.planType || "Financial Plan",
    formLink: props.formLink || "[FORM_LINK]",
    // Prefer explicit reportUrl from backend (saved via /saveReportUrl)
    reportLink: props.reportUrl || props.reportLink || "[REPORT_LINK]",
  });
  const [apiTemplates, setApiTemplates] = useState([]);
  // Track which backend email template (from /email-templates) is selected.
  // This is sent as templateId to /adminPanel/send-report-email so the backend
  // can render the full structured email with placeholders filled.
  const [selectedApiTemplateId, setSelectedApiTemplateId] = useState(null);
  const [summaryPdfReady, setSummaryPdfReady] = useState(false);
  const [summaryPdfFilename, setSummaryPdfFilename] = useState("");
  const [summaryPreviewUrl, setSummaryPreviewUrl] = useState(null);
  const [summaryGenerating, setSummaryGenerating] = useState(false);
  const [summaryComments, setSummaryComments] = useState(EMPTY_SUMMARY_COMMENTS);
  const [showSummaryComments, setShowSummaryComments] = useState(false);

  useEffect(() => {
    return () => {
      if (summaryPreviewUrl) {
        URL.revokeObjectURL(summaryPreviewUrl);
      }
    };
  }, [summaryPreviewUrl]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await apiGet("/email-templates");
        if (response.status === 200 && response.body.success) {
          const data = response.body.data;
          setApiTemplates(Array.isArray(data) ? data : [data]);
        }
      } catch (error) {
        console.error("Error fetching email templates:", error);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    setEmailList(props.emails);
  }, [props.emails]);

  // Update client details when props change
  useEffect(() => {
    console.log(
      "Props changed - clientName:",
      props.clientName,
      "planType:",
      props.planType
    );
    setClientDetails({
      name: props.clientName || "Client",
      planType: props.planType || "Financial Plan",
      formLink: props.formLink || "[FORM_LINK]",
      // Keep clientDetails in sync with latest backend-provided report URL
      reportLink: props.reportUrl || props.reportLink || "[REPORT_LINK]",
    });
  }, [props.clientName, props.planType, props.formLink, props.reportUrl, props.reportLink]);

  // Function to generate form link after report generation
  const generateFormLink = React.useCallback(() => {
    if (props.formId) {
      const formLink = `${constants.formUrl}/form/${props.formId}?role=${rolesWithId.admin}`;
      // Only update if the form link is different to avoid infinite loops
      setClientDetails((prev) => {
        if (prev.formLink !== formLink) {
          return {
            ...prev,
            formLink: formLink,
          };
        }
        return prev;
      });
      return formLink;
    }
    return "[FORM_LINK]";
  }, [props.formId]);

  // Auto-generate form link when component mounts
  useEffect(() => {
    if (props.formId) {
      generateFormLink();
    }
  }, [props.formId, generateFormLink]);

  // When a template is selected, autofill the message with client details
  useEffect(() => {
    if (selectedTemplate !== null) {
      // Generate form link first
      const formLink = generateFormLink();

      let templateHtml = TEMPLATES[selectedTemplate].html;
      let templateSubject = TEMPLATES[selectedTemplate].subject;

      console.log("Template selection - Client details:", clientDetails);
      console.log("Template selection - Form link:", formLink);
      console.log("Template selection - Template HTML:", templateHtml);

      // Replace placeholders with actual client details
      templateHtml = templateHtml
        .replace(/\[CLIENT_NAME\]/g, clientDetails.name)
        .replace(/\[FORM_LINK\]/g, formLink)
        .replace(/\[REPORT_LINK\]/g, clientDetails.reportLink);

      templateSubject = templateSubject.replace(
        /\[CLIENT_NAME\]/g,
        clientDetails.name
      );

      console.log("Template selection - Final HTML:", templateHtml);
      console.log("Template selection - Final Subject:", templateSubject);

      setMessage(templateHtml);
      setSubject(templateSubject);
      setShowTemplateModal(false);
    }
  }, [selectedTemplate, clientDetails, props.formId, props.clientName, generateFormLink]);



  // Function to resolve the report link for this form/email
  const generateReportLink = () => {
    // Special case: when used from "Send Report to the Client", always open the
    // public report view route in the form app: `${formUrl}/report/{id}`
    if (props.useReportEmailEndpoint && props.formId) {
      return `${constants.formUrl}/reports/${props.formId}`;
    }

    // 1) Prefer the backend-saved report URL (e.g., PDF saved via /saveReportUrl)
    if (props.reportUrl) {
      setClientDetails((prev) => {
        if (prev.reportLink !== props.reportUrl) {
          return { ...prev, reportLink: props.reportUrl };
        }
        return prev;
      });
      return props.reportUrl;
    }

    // 2) Fall back to any explicit reportLink prop
    if (props.reportLink) {
      setClientDetails((prev) => {
        if (prev.reportLink !== props.reportLink) {
          return { ...prev, reportLink: props.reportLink };
        }
        return prev;
      });
      return props.reportLink;
    }

    // 3) Legacy behaviour: derive report URL from formId and app URL
    if (props.formId) {
      const fallbackReportLink = `${constants.formUrl}/reports/${props.formId}?role=${rolesWithId.admin}`;
      setClientDetails((prev) => {
        if (prev.reportLink !== fallbackReportLink) {
          return { ...prev, reportLink: fallbackReportLink };
        }
        return prev;
      });
      return fallbackReportLink;
    }

    return "[REPORT_LINK]";
  };

  // Function to open report URL
  const openReport = () => {
    const reportLink = generateReportLink();
    if (reportLink !== "[REPORT_LINK]") {
      window.open(reportLink, "_blank");
    }
  };

  const maxUserAttachments = MAX_ATTACHMENTS;

  const handleGenerateSummaryPdf = async () => {
    if (!props.formId) {
      alert("Missing form id. Please save the form first.");
      return;
    }

    setSummaryGenerating(true);
    try {
      let result = await apiPostBlob("/adminPanel/report-summary-pdf", {
        formId: props.formId,
        teamComments: summaryComments,
      });

      if (result.status === 404 || result.status === 405) {
        result = await apiGetBlob(
          `/adminPanel/report-summary-pdf?formId=${props.formId}`
        );
      }

      if (result.status !== 200 || !result.blob) {
        alert(result.error || "Failed to generate summary PDF.");
        return;
      }

      const filename = result.filename || "AWAKEN-Summary.pdf";
      if (summaryPreviewUrl) {
        URL.revokeObjectURL(summaryPreviewUrl);
      }
      const previewUrl = URL.createObjectURL(result.blob);
      setSummaryPreviewUrl(previewUrl);
      setSummaryPdfFilename(filename);
      setSummaryPdfReady(true);

      const downloadLink = document.createElement("a");
      downloadLink.href = previewUrl;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (err) {
      console.error("Summary PDF generation error:", err);
      alert("An unexpected error occurred while generating the summary PDF.");
    } finally {
      setSummaryGenerating(false);
    }
  };

  const handlePreviewSummaryPdf = () => {
    if (!summaryPreviewUrl) {
      alert("Generate the summary PDF first, then preview it.");
      return;
    }
    window.open(summaryPreviewUrl, "_blank", "noopener,noreferrer");
  };

  const handleFileUpload = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    setFile((prev) => {
      const remaining = maxUserAttachments - prev.length;
      if (remaining <= 0) {
        alert(`You can attach up to ${maxUserAttachments} files.`);
        return prev;
      }
      const toAdd = selected.slice(0, remaining);
      if (selected.length > remaining) {
        alert(
          `Only ${remaining} more file(s) can be added (maximum ${maxUserAttachments}).`
        );
      }
      return [...prev, ...toAdd];
    });

    e.target.value = "";
  };

  const handleFileDelete = (id) => {
    setFile(file.filter((f, index) => index !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filter out invalid or empty emails
    const validEmails = emailList.filter(email => email && email.trim() !== "");

    if (validEmails.length === 0) {
      alert("Please enter at least one valid recipient email.");
      return;
    }

    setLoader(true);

    try {
      let response;

      // When used for "Send report to client", call the /adminPanel/send-report-email API
      if (props.useReportEmailEndpoint) {
        if (!props.formId) {
          alert(
            "Missing form id. Please save the form before sending the report email."
          );
          setLoader(false);
          return;
        }

        // Use plain text from editor as the custom message that shows before the details table
        const customMessage = editorRef.current
          ? editorRef.current.innerText || ""
          : "";

        // If any attachments are selected, first upload them to Azure and collect URLs
        let attachmentLinks = [];
        if (file && file.length > 0) {
          try {
            attachmentLinks = await uploadAttachmentsToAzure(file);
          } catch (uploadError) {
            console.error("Error uploading attachments to Azure for report email:", uploadError);
            alert(
              "Failed to upload attachments to cloud. Please try again or contact support."
            );
            setLoader(false);
            return;
          }
        }

        const body = {
          formId: props.formId,
          subject,
        };

        // If a backend email template was selected, send its id so the
        // server can render the full structured email with placeholders.
        if (selectedApiTemplateId) {
          body.templateId = selectedApiTemplateId;
        } else if (customMessage && customMessage.trim()) {
          // Only send custom message when no backend template is selected.
          // When a template is selected, the backend handles the full body
          // and we avoid duplicating the template content.
          body.message = customMessage.trim();
        }

        // If we have uploaded attachments, send their URLs so the backend
        // can include them in the email (as links or true attachments).
        if (attachmentLinks.length > 0) {
          body.attachments = attachmentLinks;
        }

        // Override recipients with all emails selected in the preview (To field)
        if (validEmails.length > 0) {
          body.to = validEmails;
        }

        console.log("Sending report email body:", body);
        response = await apiPostPut(
          body,
          "/adminPanel/send-report-email",
          "POST"
        );
      } else {
        // Default flow: send full HTML via /adminPanel/send-email (supports attachments)
        const emailContent = editorRef.current?.innerHTML || "";

        const wrappedHtml = `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  body { font-family: Arial, sans-serif; line-height: 1.6; color: #333333; margin: 0; padding: 0; }
  .email-container { max-width: 600px; margin: 0 auto; padding: 20px; font-family: Arial, sans-serif; }
  .email-header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #004aad; }
  .email-content { background-color: #ffffff; padding: 20px; border-radius: 5px; min-height: 200px; }
  .email-footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #eeeeee; text-align: center; font-size: 12px; color: #888888; }
  .company-name { color: #004aad; font-size: 24px; font-weight: bold; text-decoration: none; }
</style>
</head>
<body style="margin: 0; padding: 0; background-color: #f9f9f9;">
  <div class="email-container" style="background-color: #ffffff; margin: 20px auto; padding: 20px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
    <div class="email-header">
      <h1 class="company-name" style="margin: 0; color: #004aad;">Awaken Financial Journey</h1>
    </div>
    <div class="email-content">
      ${emailContent}
    </div>
    <div class="email-footer">
      <p>&copy; ${new Date().getFullYear()} Awaken Financial. All rights reserved.</p>
      <p style="margin-top: 5px;">This is an automated message, please do not reply directly to this email unless specified.</p>
    </div>
  </div>
</body>
</html>`;

        if (file && file.length > 0) {
          // First upload all attachments to Azure Blob Storage and collect their URLs
          let attachmentLinks = [];
          try {
            attachmentLinks = await uploadAttachmentsToAzure(file);
          } catch (uploadError) {
            console.error("Error uploading attachments to Azure:", uploadError);
            alert(
              "Failed to upload attachments to cloud. Please try again or contact support."
            );
            setLoader(false);
            return;
          }

          const body = {
            to: validEmails,
            subject,
            html: wrappedHtml,
            attachments: attachmentLinks,
          };

          console.log("Sending email with cloud attachments:", body);
          response = await apiPostPut(body, "/adminPanel/send-email", "POST");
        } else {
          const body = {
            to: validEmails,
            subject,
            html: wrappedHtml,
          };

          console.log("Sending email body:", body);
          response = await apiPostPut(body, "/adminPanel/send-email", "POST");
        }
      }

      setLoader(false);

      if (response.status === 200) {
        alert(response.body?.message || "Email sent successfully!");
        props.setMail(false);

        if (props.onStatusUpdate && props.currentStatus !== undefined) {
          const nextStatus = props.currentStatus + 1;
          await props.onStatusUpdate(nextStatus, undefined, true);
        }
      } else {
        console.error("Email sending failed:", response);
        const errorMsg = response.body?.message || "Unknown error";
        if (response.status === 403 || response.status === 401) {
          alert(
            `Permission Denied: ${errorMsg}. You may not have authorization to send emails.`
          );
        } else {
          alert(`Failed to send email. Server responded: ${errorMsg}`);
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setLoader(false);
      alert("An unexpected error occurred while sending email.");
    }
  };

  // const applyStyleToSelection = (style, value) => {
  // 	const selection = window.getSelection();
  // 	if (selection.rangeCount > 0) {
  // 		const range = selection.getRangeAt(0);
  // 		const span = document.createElement("span");
  // 		span.style[style] = value;
  // 		range.surroundContents(span);
  // 	}
  // };

  const formatText = (command) => {
    document.execCommand(command, false, null);
    setActiveFormats((prevFormats) => ({
      ...prevFormats,
      [command]: !prevFormats[command],
    }));
    // // Deselect after applying
    // setTimeout(
    // 	() => setActiveFormats({ ...activeFormats, [command]: false }),
    // 	200
    // );
  };

  const applyStyle = (command, value = null) => {
    if (command === "fontSize") {
      const sizeMap = { 10: 1, 13: 2, 16: 3, 18: 4, 24: 5, 32: 6, 48: 7 }; // Map pixel sizes to execCommand values
      const mappedSize = sizeMap[value];
      if (mappedSize) {
        document.execCommand(command, false, mappedSize);
        setFontSize(`${value}px`);
      }
    } else if (command === "fontName") {
      document.execCommand(command, false, value);
      setFontFamily(value);
    }
  };

  const addEmail = () => {
    if (newEmail && !emailList.includes(newEmail)) {
      setEmailList([...emailList, newEmail]);
      setNewEmail("");
    }
  };

  if (!props.mail) {
    return null;
  }

  return createPortal(
      <>
        {/* Template Selection Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 z-[100] backdrop-blur-lg bg-black bg-opacity-30 flex items-center justify-center text-left">
            <div className="bg-white shadow-xl rounded-lg w-[95vw] md:w-[60vw] max-w-3xl p-6 relative">
              <AiFillCloseCircle
                className="absolute top-3 right-3 text-red-500 text-2xl cursor-pointer"
                onClick={() => props.setMail(false)}
              />
              <h2 className="text-xl font-semibold mb-4">
                Select a Mail Template
              </h2>
              <div className="space-y-4">
                {TEMPLATES.map((tpl, idx) => (
                  <div key={idx} className="border rounded p-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{tpl.name}</span>
                      <div className="flex gap-2">
                        <button
                          className="text-blue-500 underline text-sm"
                          onClick={() => setPreviewIdx(idx)}
                          type="button"
                        >
                          View Full Message
                        </button>
                        <button
                          className="bg-primary text-white px-3 py-1 rounded text-sm"
                          onClick={() => setSelectedTemplate(idx)}
                          type="button"
                        >
                          Select
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-center gap-4 mt-6">
                <button
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  type="button"
                  onClick={() => {
                    setShowTemplateModal(false);

                    // When writing a custom email, prefill with a simple message
                    // that already contains the report link (if available).
                    const reportLink = generateReportLink();
                    const hasReportLink =
                      reportLink && reportLink !== "[REPORT_LINK]";

                    setSubject(
                      `Your financial plan report - ${clientDetails.name}`
                    );

                    if (hasReportLink) {
                      setMessage(
                        `<p>Dear ${clientDetails.name},</p>
<p>Please find your financial plan report at the link below:</p>
<p><a href="${reportLink}">View Report</a></p>
<p>Best regards,<br/>Awaken Financial Team</p>`
                      );
                    } else {
                      // Fallback to empty body if we don't yet know the report URL
                      setMessage("");
                    }
                  }}
                >
                  Write Custom Mail
                </button>
                {props.formId && (
                  <>
                    <button
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                      type="button"
                      onClick={openReport}
                    >
                      View Report
                    </button>
                    <button
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      type="button"
                      onClick={() => {
                        window.open(
                          `${constants.formUrl}/form/${props.formId}?role=${rolesWithId.admin}`,
                          "_blank"
                        );
                      }}
                    >
                      View/Edit Form
                    </button>
                  </>
                )}
              </div>
              {/* Email-like Preview Modal */}
              {previewIdx !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                  <div className="bg-white rounded-lg shadow-lg w-[99vw] md:w-[90vw] lg:w-[70vw] max-w-6xl p-8 relative min-h-[60vh] flex flex-col">
                    <button
                      className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl"
                      onClick={() => setPreviewIdx(null)}
                    >
                      &times;
                    </button>
                    <h3 className="text-2xl font-semibold mb-4">
                      {TEMPLATES[previewIdx].name}
                    </h3>
                    <div className="mb-2">
                      <span className="font-semibold">Subject:</span>{" "}
                      {TEMPLATES[previewIdx].subject.replace(
                        /\[CLIENT_NAME\]/g,
                        clientDetails.name
                      )}
                    </div>
                    <div
                      className="border rounded p-4 bg-gray-50 min-h-[300px] text-left overflow-y-auto overflow-x-hidden flex-1 prose max-w-none break-words whitespace-pre-line"
                      style={{ fontFamily: "Arial, sans-serif" }}
                      dangerouslySetInnerHTML={{
                        __html: TEMPLATES[previewIdx].html
                          .replace(/\[CLIENT_NAME\]/g, clientDetails.name)
                          .replace(/\[FORM_LINK\]/g, generateFormLink())
                          .replace(
                            /\[REPORT_LINK\]/g,
                            clientDetails.reportLink
                          ),
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Main Mail Popup */}
        {!showTemplateModal && (
          <div className="fixed inset-0 z-[100] backdrop-blur-sm bg-black/40 flex items-center justify-center p-3 sm:p-4 text-left">
            <div
              className="compose-email-modal bg-white shadow-xl rounded-xl w-full max-w-3xl h-[min(720px,92vh)] min-h-[420px] flex flex-col relative overflow-hidden mx-auto text-left"
              role="dialog"
              aria-labelledby="compose-email-title"
            >
              <AiFillCloseCircle
                className="absolute top-3 right-3 text-red-500 text-2xl cursor-pointer z-10"
                onClick={() => props.setMail(false)}
              />
              <div className="flex-shrink-0 flex items-center gap-3 border-b px-5 py-3 pr-12 text-left">
                <MdMailOutline className="text-primary text-2xl shrink-0" />
                <h1 id="compose-email-title" className="text-lg font-semibold">
                  Compose Email
                </h1>
              </div>
              <form
                onSubmit={handleSubmit}
                className="flex flex-col flex-1 min-h-0 overflow-hidden text-left"
              >
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4 space-y-4 text-left">
                <div className="w-full text-left">
                  <label className="block text-sm font-semibold text-gray-700 mb-1 text-left">
                    Template
                  </label>
                  <select
                    className="w-full p-2 border rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onChange={async (e) => {
                      const selectedId = e.target.value;
                      if (!selectedId) return;

                      // Remember which backend template is selected so we can
                      // send templateId to /adminPanel/send-report-email.
                      setSelectedApiTemplateId(selectedId);

                      const template = apiTemplates.find((t) => t.id === selectedId);
                      if (!template) return;

                      // Use server-side rendering so preview matches the sent email
                      if (props.useReportEmailEndpoint && props.formId) {
                        try {
                          const previewResponse = await apiGet(
                            `/adminPanel/email-template-preview?formId=${props.formId}&templateId=${selectedId}`
                          );
                          if (
                            previewResponse.status === 200 &&
                            previewResponse.body?.success &&
                            previewResponse.body?.data
                          ) {
                            setSubject(previewResponse.body.data.subject || "");
                            setMessage(previewResponse.body.data.body || "");
                            return;
                          }
                        } catch (previewErr) {
                          console.error(
                            "Failed to load email template preview:",
                            previewErr
                          );
                        }
                      }

                      const advisorName = clientsStore.user?.name || "Advisor";
                      let subject = template.subjectTemplate || "";
                      let body = template.bodyTemplateHtml || "";
                      const clientName = clientDetails.name;
                      subject = subject.replace(/{{client\.name}}/gi, clientName);
                      body = body.replace(/{{client\.name}}/gi, clientName);
                      subject = subject.replace(/{{advisor\.name}}/gi, advisorName);
                      body = body.replace(/{{advisor\.name}}/gi, advisorName);
                      if (!/<[a-z][\s\S]*>/i.test(body)) {
                        body = body.replace(/\n/g, "<br/>");
                      }
                      setSubject(subject);
                      setMessage(body);
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Select a template...
                    </option>
                    {apiTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-full text-left border-b border-gray-100 pb-3">
                  <label
                    htmlFor="compose-email-to"
                    className="block text-sm font-semibold text-gray-700 mb-1.5 text-left"
                  >
                    To
                  </label>
                  {emailList.length > 0 && (
                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto mb-2 justify-start">
                      {emailList.map((mail, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-1 bg-gray-100 border border-gray-200 rounded-md text-sm text-left relative pr-7"
                        >
                          {mail}
                          <AiFillCloseCircle
                            className="hover:text-red-500 text-red-300 text-base absolute top-0.5 right-0.5 cursor-pointer"
                            onClick={() =>
                              setEmailList(emailList.filter((e) => e !== mail))
                            }
                          />
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2 w-full">
                    <input
                      type="email"
                      id="compose-email-to"
                      placeholder="Enter email address"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="p-2 border rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-w-0"
                    />
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm whitespace-nowrap shrink-0 hover:bg-blue-700"
                      onClick={addEmail}
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div className="w-full text-left border-b border-gray-100 pb-3">
                  <label
                    htmlFor="compose-email-subject"
                    className="block text-sm font-semibold text-gray-700 mb-1.5 text-left"
                  >
                    Subject
                  </label>
                  <input
                    id="compose-email-subject"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full p-2 border rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="w-full text-left">
                  <label className="block text-sm font-semibold text-gray-700 mb-2 text-left">
                    Message
                  </label>
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      className={`px-2 py-1 rounded ${activeFormats.bold
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200"
                        }`}
                      onClick={() => formatText("bold")}
                    >
                      B
                    </button>
                    <button
                      type="button"
                      className={`px-2 py-1 rounded ${activeFormats.italic
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200"
                        }`}
                      onClick={() => formatText("italic")}
                    >
                      I
                    </button>
                    <button
                      type="button"
                      className={`px-2 py-1 rounded ${activeFormats.underline
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200"
                        }`}
                      onClick={() => formatText("underline")}
                    >
                      U
                    </button>
                    <input
                      type="number"
                      className="px-2 py-1 border rounded"
                      value={parseInt(fontSize)}
                      min="10"
                      max="48"
                      step="2"
                      onChange={(e) =>
                        applyStyle("fontSize", parseInt(e.target.value))
                      }
                    />
                    <select
                      className="px-2 py-1 border rounded"
                      value={fontFamily}
                      onChange={(e) => applyStyle("fontName", e.target.value)}
                    >
                      <option value="Arial">Arial</option>
                      <option value="Verdana">Verdana</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Courier New">Courier New</option>
                    </select>
                  </div>
                  <div
                    ref={editorRef}
                    contentEditable={true}
                    className="w-full p-2 border text-gray-700 rounded-lg focus:outline-none min-h-[140px] max-h-[220px] text-left overflow-y-auto overflow-x-hidden break-words whitespace-pre-line bg-white resize-none"
                    style={{ fontSize, fontFamily }}
                    dangerouslySetInnerHTML={{ __html: message }}
                  ></div>
                </div>

                  {props.useReportEmailEndpoint &&
                    props.formId &&
                    showSummaryComments && (
                      <div className="mt-4 p-3 border border-indigo-200 rounded-lg bg-indigo-50/30 text-left">
                        <p className="text-sm font-semibold text-indigo-950 text-left mb-2">
                          Summary PDF — planner comments
                        </p>
                        <div className="grid grid-cols-1 gap-3">
                          {SUMMARY_COMMENT_FIELDS.map(({ key, label }) => (
                            <label
                              key={key}
                              className="block text-left w-full"
                            >
                              <span className="block text-xs font-medium text-gray-700 mb-1 text-left">
                                {label}
                              </span>
                              <textarea
                                rows={2}
                                className="w-full text-sm text-left border border-gray-200 rounded-md p-2 bg-white"
                                value={summaryComments[key]}
                                onChange={(e) =>
                                  setSummaryComments((prev) => ({
                                    ...prev,
                                    [key]: e.target.value,
                                  }))
                                }
                                placeholder="Optional comment for this section"
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                  {props.useReportEmailEndpoint && props.formId && (
                    <p className="text-xs text-gray-500 text-left mt-2">
                      {summaryPdfReady
                        ? `Summary PDF ready (${summaryPdfFilename}). Preview or attach via Attach File.`
                        : "Optional: Summary comments → generate PDF → attach before sending."}
                    </p>
                  )}
                </div>

                <div className="flex-shrink-0 border-t border-gray-200 px-5 py-3 bg-white space-y-2.5 text-left">
                  <div className="flex flex-wrap items-center gap-2 justify-start">
                    <label
                      className={`inline-flex items-center gap-2 text-sm ${
                        file.length >= maxUserAttachments
                          ? "cursor-not-allowed text-gray-400"
                          : "cursor-pointer text-primary"
                      }`}
                    >
                      <AiOutlineUpload className="text-lg shrink-0" />
                      Attach File
                      <span className="text-xs text-gray-500">
                        (max {maxUserAttachments})
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        multiple
                        disabled={file.length >= maxUserAttachments}
                        onChange={handleFileUpload}
                      />
                    </label>
                    {file?.length > 0 &&
                      file.map((uploadedFile, index) => (
                        <span
                          key={`${uploadedFile?.name}-${index}`}
                          className="px-2 py-1 bg-white border rounded text-xs relative pr-5 max-w-[180px] truncate"
                          title={uploadedFile?.name}
                        >
                          {uploadedFile?.name || "Attachment"}
                          <AiFillCloseCircle
                            className="hover:text-red-500 text-red-300 text-base absolute -top-1.5 -right-1.5 cursor-pointer"
                            onClick={() => handleFileDelete(index)}
                          />
                        </span>
                      ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 justify-between pt-1 border-t border-gray-100">
                    <div className="flex flex-wrap gap-1.5 justify-start">
                      {props.formId && props.useReportEmailEndpoint && (
                        <>
                          <button
                            type="button"
                            className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                            onClick={() =>
                              setShowSummaryComments((open) => !open)
                            }
                          >
                            {showSummaryComments
                              ? "Hide comments"
                              : "Summary comments"}
                          </button>
                          <button
                            type="button"
                            className="px-2.5 py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                            disabled={summaryGenerating}
                            onClick={handleGenerateSummaryPdf}
                          >
                            {summaryGenerating ? "Generating…" : "Summary PDF"}
                          </button>
                          <button
                            type="button"
                            className="px-2.5 py-1.5 text-xs bg-indigo-100 text-indigo-900 rounded-md hover:bg-indigo-200 disabled:opacity-50"
                            disabled={!summaryPdfReady}
                            onClick={handlePreviewSummaryPdf}
                          >
                            Preview
                          </button>
                        </>
                      )}
                      {props.formId && (
                        <>
                          <button
                            type="button"
                            className="px-2.5 py-1.5 text-xs text-green-700 border border-green-200 rounded-md hover:bg-green-50"
                            onClick={openReport}
                          >
                            View report
                          </button>
                          <button
                            type="button"
                            className="px-2.5 py-1.5 text-xs text-sky-700 border border-sky-200 rounded-md hover:bg-sky-50"
                            onClick={() => {
                              window.open(
                                `${constants.formUrl}/form/${props.formId}?role=${rolesWithId.admin}`,
                                "_blank"
                              );
                            }}
                          >
                            View form
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      type="submit"
                      className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm font-medium shrink-0"
                    >
                      {loader ? "Sending…" : "Send mail"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </>,
    document.body
  );
}

// import React, { useEffect, useState } from "react";
// import { AiFillCloseCircle, AiOutlineUpload } from "react-icons/ai";
// import {
// 	MdClose,
// 	MdMailOutline,
// 	MdOutlineAddCircleOutline,
// } from "react-icons/md";
// import { useNavigate } from "react-router-dom";
// import { apiPostPut } from "../../api/api_methods";
// import Loader from "../reusableComponents/loader";

// export default function MailPreview(props) {
// 	const [emailList, setEmailList] = useState(
// 		props.emails || [
// 			"example1@example.com",
// 			"example2@example.com",
// 			"example3@example.com",
// 		]
// 	);
// 	const navigate = useNavigate();
// 	const [form, setForm] = useState(false);
// 	const [fileName1, setFileName1] = useState("");
// 	const [url, setUrl] = useState("");
// 	const [loader, setLoader] = useState(false);
// 	const [close, setClose] = useState("");

// 	useEffect(() => {
// 		setEmailList(props.emails);
// 	}, [props.emails]);

// 	const closeThisPopUP = () => {
// 		setForm(false);
// 		setFileName1("");
// 		setUrl("");
// 		props.setMail(false); // Assuming setMail is passed as a prop
// 	};

// 	return (
// 		<>
// 			{props.mail ? (
// 				<div className="absolute w-full h-full z-50 inset-0 backdrop-filter backdrop-blur-sm bg-black bg-opacity-10">
// 					<div className="fixed inset-0 bg-[#FFFFFF] z-30 flex flex-col justify-center rounded-xl items-center md:w-[65%] lg:w-[60%] w-[95%] h-[90%] md:h-[95%] lg:h-[95%] m-auto overflow-y-auto no-scrollbar">
// 						<form
// 							className="flex flex-col justify-around items-center h-full py-5 w-[100%] md:w-[100%] lg:[100%] "
// 							onSubmit={async (e) => {
// 								e.preventDefault();
// 								setLoader(true);
// 								const body = {
// 									mails: emailList,
// 									subject: e.target.sub.value,
// 									text: e.target.message.value,
// 								};
// 								const response = await apiPostPut(
// 									body,
// 									"/company-dashboard/send-multiple-emails",
// 									"POST"
// 								);
// 								if (response.status === 200) {
// 									setLoader(false);
// 									alert(response.body.message);
// 									props.setMail(false); // Assuming setMail is passed as a prop
// 								} else {
// 									setLoader(false);
// 									alert(response.body.message);
// 								}
// 							}}
// 						>
// 							<Loader loader={loader} />
// 							<AiFillCloseCircle
// 								className="text-primary absolute text-2xl cursor-pointer top-2 right-2"
// 								onClick={closeThisPopUP}
// 							/>

// 							<span className="flex items-center gap-2 px-2 md:px-10 w-full">
// 								<span className="w-10 h-10 bg-primary2 rounded-full flex items-center justify-center">
// 									<MdMailOutline className="w-5 h-5" />
// 								</span>
// 								<h1 className="text-lg font-semibold w-[90%] md:w-[70%]">
// 									{/* Send mail to {props.name || "Sample"} Candidates */}
// 									Report Preview
// 								</h1>
// 							</span>
// 							<div className="rounded-xl relative w-[95%] h-[80%]">
// 								<span className="py-2 flex items-center mx-[2.5%] border-b-2 w-[95%]">
// 									<label className="text-gray-500 font-semibold w-10">
// 										To :
// 									</label>
// 									<div
// 										className="flex flex-row items-center w-[80%] overflow-x-scroll h-14 gap-2 px-2"
// 										style={{
// 											scrollbarWidth: "none",
// 										}}
// 									>
// 										{Array.isArray(emailList) &&
// 											emailList?.map((mail, index) => (
// 												<span
// 													key={index}
// 													className="px-3 py-1 my-3 bg-gray-200 rounded-lg relative text-sm text-gray-700"
// 													onMouseEnter={() => {
// 														setClose(index);
// 													}}
// 													onMouseLeave={() => setClose("")}
// 												>
// 													<AiFillCloseCircle
// 														className="text-primary md:hidden z-50 absolute text-lg cursor-pointer -top-2 -right-2"
// 														onClick={() => {
// 															setEmailList(emailList.filter((e) => e !== mail));
// 														}}
// 													/>
// 													{close === index ? (
// 														<AiFillCloseCircle
// 															className="text-primary z-50 absolute text-lg cursor-pointer -top-2 -right-2"
// 															onClick={() => {
// 																setEmailList(
// 																	emailList.filter((e) => e !== mail)
// 																);
// 															}}
// 														/>
// 													) : (
// 														<></>
// 													)}
// 													{mail}
// 												</span>
// 											))}
// 									</div>
// 									<span className="ml-2">
// 										<button
// 											type="button"
// 											className="px-3 py-2 w-24 bg-orange-100 text-orange-500 rounded-lg text-sm"
// 											onClick={() => {
// 												const res = prompt("Enter Email ");
// 												if (res) {
// 													setEmailList([...emailList, res]);
// 												}
// 											}}
// 										>
// 											+ Add Mail
// 										</button>
// 									</span>
// 								</span>
// 								<span className="py-2 flex items-center mx-[2.5%] w-[95%]">
// 									<label className="text-gray-500 font-semibold">Subject</label>
// 									<input
// 										name="sub"
// 										id="sub"
// 										type="text"
// 										defaultValue={props.sub || "Sample Subject"}
// 										className="w-[80%] ml-4 focus:outline-none"
// 									/>
// 								</span>
// 								<textarea
// 									className="resize-none w-[95%] h-[80%] mx-[2.5%] focus:outline-none"
// 									style={{ scrollbarWidth: "none" }}
// 									name="message"
// 									id="message"
// 									defaultValue={props.mess || "Sample message content..."}
// 								/>
// 							</div>
// 							<div className="w-[80%] flex justify-end md:mt-2">
// 								<button
// 									type="submit"
// 									className="px-4 bg-primary text-white py-1 rounded-xl"
// 								>
// 									Send Mail
// 								</button>
// 							</div>
// 						</form>
// 					</div>
// 				</div>
// 			) : null}
// 		</>
// 	);
// }
